/**
 * OKRHub Product Section Component
 * Showcases the OKRHub software product with features and benefits
 */

import React from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { Button } from './Button';
import {
  Target,
  GitBranch,
  RefreshCw,
  BarChart3,
  Users2,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Lightbulb
} from 'lucide-react';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const coreFeatures: Feature[] = [
  {
    icon: <GitBranch className="w-6 h-6" />,
    title: 'Clear OKR Hierarchy',
    description: 'Visualize alignment from Company to Team to Individual objectives with intuitive cascading views.'
  },
  {
    icon: <RefreshCw className="w-6 h-6" />,
    title: 'Simple Check-ins',
    description: 'Regular progress updates that take minutes, not hours. Keep momentum without bureaucracy.'
  },
  {
    icon: <Target className="w-6 h-6" />,
    title: 'Ownership Tracking',
    description: 'Every objective has a clear owner. No ambiguity about who is accountable for what.'
  },
  {
    icon: <BarChart3 className="w-6 h-6" />,
    title: 'Review-Ready Insights',
    description: 'Dashboards built for meaningful conversations, not just data collection.'
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: 'Contextual Comments',
    description: 'Discuss progress, blockers, and learnings right where the work happens.'
  },
  {
    icon: <Lightbulb className="w-6 h-6" />,
    title: 'Reflection & Learning',
    description: 'Built-in prompts for quarterly retrospectives. Learn from every cycle.'
  }
];

const whyOKRHub = [
  {
    title: 'Designed for Thinking, Not Just Tracking',
    description: 'Most OKR tools are glorified spreadsheets. OKRHub is designed to support strategic thinking and meaningful reviews.'
  },
  {
    title: 'Built by Practitioners',
    description: 'Created by consultants who have implemented OKRs in real organizations. We know what actually works.'
  },
  {
    title: 'Clutter-Free Interface',
    description: 'No unnecessary features or complexity. Just what you need to run an effective OKR program.'
  },
  {
    title: 'Consulting-Backed Support',
    description: 'Get expert guidance when you need it. Our consulting team is always available to help you succeed.'
  }
];

export const OKRHubProductSection: React.FC = () => {
  return (
    <Section id="okrhub" background="white" padding="xl">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
          <Target className="w-4 h-4" />
          <span>OKR Hub Software</span>
        </div>

        <h2 className="font-display text-4xl md:text-5xl text-ink mb-6">
          A Tool That Supports Thinking,<br />
          <span className="text-primary-600">Not Just Scoring</span>
        </h2>

        <p className="text-xl text-ink-muted max-w-3xl mx-auto leading-relaxed">
          OKR Hub is not another project management tool dressed up as an OKR platform.
          It's purpose-built to help teams create clarity, maintain focus, and drive execution.
        </p>
      </div>

      {/* Product Visual + Why OKRHub */}
      <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
        {/* Left: Product Visual */}
        <div className="relative">
          <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-2xl p-8 border border-stone-200">
            {/* Mock Dashboard */}
            <div className="bg-white rounded-xl shadow-soft-md p-6 border border-stone-200">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-stone-200 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-ink">Company OKRs</h4>
                    <p className="text-xs text-ink-muted">Q1 2025</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users2 className="w-4 h-4 text-ink-muted" />
                  <span className="text-sm text-ink-muted">12 team members</span>
                </div>
              </div>

              {/* OKR Hierarchy Visual */}
              <div className="space-y-4">
                {/* Company Level */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      C
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold text-ink text-sm">Achieve market leadership in target segment</h5>
                        <span className="text-xs font-semibold text-primary-600">72%</span>
                      </div>
                      <div className="h-1.5 bg-primary-200 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full" style={{ width: '72%' }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Level */}
                <div className="ml-6 space-y-3">
                  <div className="bg-white border border-stone-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        S
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-ink">Sales Team</span>
                          <span className="text-xs font-semibold text-blue-600">85%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-stone-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        P
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-ink">Product Team</span>
                          <span className="text-xs font-semibold text-green-600">68%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-stone-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        M
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-ink">Marketing Team</span>
                          <span className="text-xs font-semibold text-orange-600">62%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Stats */}
              <div className="grid grid-cols-3 gap-3 mt-6 pt-4 border-t border-stone-200">
                <div className="text-center">
                  <div className="text-lg font-display text-ink">8</div>
                  <div className="text-xs text-ink-muted">Objectives</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-display text-ink">24</div>
                  <div className="text-xs text-ink-muted">Key Results</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-display text-ink">72%</div>
                  <div className="text-xs text-ink-muted">On Track</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Why OKRHub */}
        <div>
          <h3 className="font-display text-2xl text-ink mb-8">
            Why Choose OKR Hub?
          </h3>

          <div className="space-y-6">
            {whyOKRHub.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-success-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-ink mb-1">{item.title}</h4>
                  <p className="text-ink-muted text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button size="lg" variant="primary" onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}>
              Request a Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" as="a" href="/login">
              Sign In
            </Button>
          </div>
        </div>
      </div>

      {/* Core Features */}
      <SectionHeader
        subtitle="Platform Capabilities"
        title="Everything You Need, Nothing You Don't"
        description="Focused features that support your OKR discipline without adding complexity."
        align="center"
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {coreFeatures.map((feature, index) => (
          <div
            key={index}
            className="bg-stone-50 rounded-xl p-6 border border-stone-200 hover:border-primary-300 hover:bg-white hover:shadow-soft-md transition-all duration-300"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 mb-4">
              {feature.icon}
            </div>
            <h4 className="font-semibold text-ink mb-2">{feature.title}</h4>
            <p className="text-ink-muted text-sm leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* Bottom Note */}
      <div className="mt-16 bg-primary-50 rounded-2xl p-8 md:p-12 text-center">
        <h3 className="font-display text-2xl text-ink mb-4">
          Software Alone Won't Transform Your Organization
        </h3>
        <p className="text-ink-muted max-w-2xl mx-auto mb-6 leading-relaxed">
          OKR Hub software works best when paired with our consulting or managed services.
          The tool supports the system—but people and process drive the results.
        </p>
        <Button
          variant="ghost"
          className="text-primary-600 hover:text-primary-700"
          onClick={() => {
            const element = document.getElementById('services');
            if (element) element.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Explore Our Services
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </Section>
  );
};
